import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Briefcase, Handshake, UserPlus, Users, DollarSign, Landmark, Megaphone,
  Plus, Clock, ChevronRight, MessageSquare, BookOpen, ExternalLink
} from "lucide-react";
import { toast } from "sonner";

const OPPORTUNITY_ICONS = {
  partnership: Handshake,
  referral: UserPlus,
  hiring: Users,
  funding: DollarSign,
  government: Landmark,
  marketing: Megaphone
};

export function MarketplaceZone({
  opportunities,
  opportunityFilter,
  setOpportunityFilter,
  fetchOpportunities,
  expandedOppId,
  setExpandedOppId,
  setShowPostOpportunity,
  openProfileCard,
  setActiveZone,
  formatDate
}) {
  return (
    <div className="space-y-6">
      {/* How-to Instructions Card */}
      <Card className="bg-gradient-to-r from-orange-500/10 to-neutral-900/50 border-orange-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-orange-400" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white mb-2">How to Use the Opportunity Marketplace</h3>
              <ul className="text-sm text-neutral-300 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold">1.</span>
                  <span><strong>Post your opportunity</strong> - Click "Post Opportunity" and clearly describe what you're looking for</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold">2.</span>
                  <span><strong>Filter listings</strong> - Use the dropdown to find opportunities matching your interests</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold">3.</span>
                  <span><strong>Review details</strong> - Check the description, ideal partner profile, and timeline before reaching out</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold">4.</span>
                  <span><strong>Make contact</strong> - Use the provided contact method to express your interest professionally</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={opportunityFilter} onValueChange={(v) => { setOpportunityFilter(v); fetchOpportunities(v); }}>
          <SelectTrigger className="w-48 bg-neutral-800 border-neutral-700 text-white">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent className="bg-neutral-800 border-neutral-700">
            <SelectItem value="all">All Opportunities</SelectItem>
            <SelectItem value="partnership">Partnerships</SelectItem>
            <SelectItem value="referral">Referrals</SelectItem>
            <SelectItem value="hiring">Hiring</SelectItem>
            <SelectItem value="funding">Funding</SelectItem>
            <SelectItem value="government">Government</SelectItem>
            <SelectItem value="marketing">Marketing</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={() => setShowPostOpportunity(true)}
          className="bg-[#39FF14] text-black hover:bg-[#32e612]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Post Opportunity
        </Button>
      </div>

      {/* Opportunities Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {opportunities.length > 0 ? opportunities.map((opp) => {
          const Icon = OPPORTUNITY_ICONS[opp.opportunity_type] || Briefcase;
          return (
            <Card key={opp.opportunity_id} className="bg-neutral-800/50 border-neutral-700 hover:border-[#39FF14]/30 transition-all cursor-pointer" onClick={() => setExpandedOppId(expandedOppId === opp.opportunity_id ? null : opp.opportunity_id)}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#39FF14]/10 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{opp.title}</h3>
                      <p className="text-xs text-neutral-400">by <span className="cursor-pointer hover:text-[#39FF14] transition-colors" onClick={(e) => { e.stopPropagation(); openProfileCard(opp.user_id); }}>{opp.user_name}</span> &bull; {formatDate(opp.created_at)}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="border-[#39FF14]/50 text-[#39FF14] text-xs">
                    {opp.status}
                  </Badge>
                </div>
                <p className="text-sm text-neutral-300 mb-4">{opp.description}</p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-neutral-500">Looking for:</span>
                    <span className="text-white">{opp.looking_for}</span>
                  </div>
                  {opp.timeline && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-neutral-500" />
                      <span className="text-neutral-400">{opp.timeline}</span>
                    </div>
                  )}
                </div>
                {/* Expanded Details */}
                {expandedOppId === opp.opportunity_id && (
                  <div className="mt-4 pt-4 border-t border-neutral-700 space-y-3 text-sm">
                    {opp.ideal_partner && (
                      <div>
                        <span className="text-neutral-500 block mb-1">Ideal Partner:</span>
                        <span className="text-white">{opp.ideal_partner}</span>
                      </div>
                    )}
                    {opp.contact_method && (
                      <div>
                        <span className="text-neutral-500 block mb-1">How to Reach Out:</span>
                        <span className="text-[#39FF14]">{opp.contact_method}</span>
                      </div>
                    )}
                    {opp.industry_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {opp.industry_tags.map((tag, i) => (
                          <Badge key={i} variant="outline" className="border-neutral-600 text-neutral-400 text-xs">{tag}</Badge>
                        ))}
                      </div>
                    )}
                    <Button
                      className="w-full mt-2 bg-[#39FF14] text-black hover:bg-[#32e612]"
                      onClick={(e) => { e.stopPropagation(); setActiveZone("messages"); toast.info("Send a direct message to connect about this opportunity"); }}
                      data-testid={`respond-opp-${opp.opportunity_id}`}
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Send Message to Connect
                    </Button>
                  </div>
                )}
                {expandedOppId !== opp.opportunity_id && (
                  <div className="w-full mt-4 text-center text-xs text-neutral-500 flex items-center justify-center gap-1">
                    <ChevronRight className="w-3 h-3" />
                    Click to view details & respond
                  </div>
                )}
              </CardContent>
            </Card>
          );
        }) : (
          <div className="col-span-2 text-center py-12 text-neutral-500">
            <Briefcase className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p className="text-lg mb-2">No opportunities yet</p>
            <p className="text-sm mb-4">Be the first to post an opportunity!</p>
            <Button onClick={() => setShowPostOpportunity(true)} className="bg-[#39FF14] text-black">
              <Plus className="w-4 h-4 mr-2" />
              Post First Opportunity
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
